import { useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { LandingSection } from 'modules/Authentication/utils/setDefaultLandingPage';
import { getIsFromGuestServer } from 'modules/Workspaces/WorkspacesProvider/utils';
import { APP_ROUTES } from 'routes/routePaths';
import { isGuestModeEnabled } from 'utils/utils';

import useDefaultLandingPagePreference from './useDefaultLandingPagePreference';

const SECTION_TO_ROUTE: Record<LandingSection, string> = {
  messenger: APP_ROUTES.messenger,
  calls: APP_ROUTES.meetings,
  calendar: APP_ROUTES.calendar,
};

const isRootPath = (pathname: string) => pathname === '/' || pathname === '';

/**
 * Redirects the user from `/` to their preferred landing section.
 *
 * Reads the preference from `defaultLandingPageStore` via the shared hook —
 * the same source `LayoutTab` writes to — so the page the user lands on and
 * the card shown selected in Settings are always derived from the same state.
 *
 * Navigation uses `useLayoutEffect` so it fires before the browser paints,
 * preventing a flash of the default route content when the user navigates to
 * `/`. Navigation is still deferred until the store reports `isHydrated`,
 * which waits for `matrixClient.isInitialSyncComplete()`, preventing a race
 * where account data has not yet been loaded into the local cache.
 *
 * Guests share the same preference-driven navigation as everyone else, except
 * the messenger section is hidden for them: a guest whose stored preference is
 * the 'messenger' default is routed to the meetings ('calls') section instead.
 * This mirrors the display fallback in `LayoutTab`, so the page a guest lands on
 * and the card shown selected in Settings always agree.
 */
const useDefaultLandingPage = (): void => {
  const { matrixClient } = useMatrixContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { preference, isHydrated } = useDefaultLandingPagePreference();

  useLayoutEffect(() => {
    if (!isRootPath(location.pathname)) return;
    if (!isHydrated) return;

    const isGuestUser = isGuestModeEnabled() && getIsFromGuestServer(matrixClient.getUserId() ?? '');
    const effectivePreference: LandingSection = isGuestUser && preference === 'messenger' ? 'calls' : preference;

    navigate(SECTION_TO_ROUTE[effectivePreference], { replace: true });
  }, [matrixClient, navigate, location.pathname, isHydrated, preference]);
};

export default useDefaultLandingPage;
